<template>
  <MovableDialog
    v-if="canShowChat"
    class="player-chat modal"
    :position="userPreferences.chat.position"
    :isOpen="chatStore.open"
    :hasNotif="hasChatNotif"
    :title="t('chat.title')"
    @toggle="handleToggle"
  >
    <div v-if="!sessionStore.isPlayerOrSpectator" class="chat-options">
      <label for="allowWhisper" @click="grimoire.setAllowWhisper(!grimoire.isWhisperAllowed)">
        <font-awesome-icon id="allowWhisper" :icon="['fas', grimoire.isWhisperAllowed ? 'check-square' : 'square']" />
        {{ t("menu.allowWhisper") }}
      </label>
      <label for="allowSignalHistory" @click="grimoire.setAllowSignalHistory(!grimoire.isSignalHistoryAllowed)">
        <font-awesome-icon id="allowSignalHistory"
          :icon="['fas', grimoire.isSignalHistoryAllowed ? 'check-square' : 'square']" />
        {{ t("menu.allowSignalHistory") }}
      </label>
    </div>
    <div class="chat-container">
      <ul class="tabs">
        <li v-if="sessionStore.isPlayerOrSpectator && grimoire.isWhisperAllowed && sessionStore.isSeated" aria-role="tab" class="tab" :class="{
          active: chatStore.activeTab === 'left',
          notif:
            chatStore.messages['left']?.length &&
            !chatStore.messages['left']?.at(-1)?.isRead,
        }" @click="chatStore.activeTab = 'left'">
          {{ leftNeighbor?.name }}
        </li>
        <li aria-role="tab" class="tab" :class="{
          active: chatStore.activeTab === 'global',
          notif:
            chatStore.messages['global']?.length &&
            !chatStore.messages['global']?.at(-1)?.isRead,
        }" @click="chatStore.activeTab = 'global'">
          {{ t("chat.global") }}
        </li>
        <li v-if="!sessionStore.isPlayerOrSpectator || sessionStore.isSeated" aria-role="tab" class="tab" :class="{
          active: chatStore.activeTab === 'host',
          notif:
            chatStore.messages['host']?.length &&
            !chatStore.messages['host']?.at(-1)?.isRead,
        }" @click="chatStore.activeTab = 'host'">
          {{
            sessionStore.isPlayerOrSpectator
              ? t("chat.host")
              : t("chat.players")
          }}
        </li>
        <li v-if="!sessionStore.isPlayerOrSpectator" aria-role="tab" class="tab" :class="{
          active: chatStore.activeTab === 'logs',
          notif:
            chatStore.messages['logs']?.length &&
            !chatStore.messages['logs']?.at(-1)?.isRead,
        }" @click="chatStore.activeTab = 'logs'">
          {{ t("chat.logs") }}
        </li>
        <li v-if="!sessionStore.isPlayerOrSpectator && grimoire.isWhisperAllowed" aria-role="tab" class="tab" :class="{
          active: chatStore.activeTab === 'whispers',
          notif:
            chatStore.messages['whispers']?.length &&
            !chatStore.messages['whispers']?.at(-1)?.isRead,
        }" @click="chatStore.activeTab = 'whispers'">
          {{ t("chat.whispers") }}
        </li>
        <li v-if="sessionStore.isPlayerOrSpectator && grimoire.isWhisperAllowed && sessionStore.isSeated" aria-role="tab" class="tab" :class="{
          active: chatStore.activeTab === 'right',
          notif:
            chatStore.messages['right']?.length &&
            !chatStore.messages['right']?.at(-1)?.isRead,
        }" @click="chatStore.activeTab = 'right'">
          {{ rightNeighbor?.name }}
        </li>
      </ul>

      <div ref="messagesContainer" class="messages">
        <div v-for="(msg, index) in activeMessages" :key="index">
          <div v-if="
            !msg.isRead &&
            activeMessages.slice(0, index).every((m) => m.isRead)
          " class="new-separator">
            <span>{{ t("chat.newMessages") }}</span>
          </div>
          <div :class="{
            message: true,
            own: msg.fromName === sessionStore.currentPlayerName,
            other: msg.fromName !== sessionStore.currentPlayerName,
            host: msg.fromId === 'host',
            system: msg.fromId === '',
          }">
            <strong v-if="msg.fromName !== sessionStore.currentPlayerName" :role="chatStore.activeTab === 'host' &&
              !sessionStore.isPlayerOrSpectator
              ? 'button'
              : ''
              " @click="chatStore.targetPlayer = msg.fromId">{{ msg.fromName }} <small v-if="getCharacter(msg.fromId)">({{ getCharacter(msg.fromId) }})</small>
              <template v-if="chatStore.activeTab !== 'whispers' && msg.fromId !== '' && msg.fromId !== 'system'">
                :
              </template>
            </strong>
            <em v-if="
              msg.fromId !== ''
              && (
                (
                  chatStore.activeTab === 'host'
                  && !sessionStore.isPlayerOrSpectator
                  && msg.fromName === sessionStore.currentPlayerName
                )
                || chatStore.activeTab === 'whispers'
                || (
                  chatStore.activeTab === 'logs'
                  && msg.toId !== ''
                  && msg.toId !== 'host'
                )
              )
            ">
              {{ `${t("chat.to")} ${msg.toName}` }} <small v-if="getCharacter(msg.toId)">({{ getCharacter(msg.toId) }})</small>:
            </em>

            {{ msg.text }}
            <font-awesome-icon v-if="msg.acknowledged && !sessionStore.isPlayerOrSpectator" :icon="['fas', 'check']" class="ack-check"
              :title="t('modal.signals.acknowledged')" />
          </div>
        </div>
      </div>

      <div v-if="
        chatStore.activeTab !== 'whispers' && (chatStore.activeTab !== 'logs' || !sessionStore.isPlayerOrSpectator)
      " class="input-area">
        <input v-if="!sessionStore.isPlayerOrSpectator || sessionStore.isSeated" v-model="messageInput" type="text" :placeholder="isInputDisabled && grimoire.gamePhase === 'firstNight' ? t('chat.firstNightDisabled'):t('chat.typeMessage')" :disabled="isInputDisabled"
          @keyup.enter="sendMessage" />

        <button v-if="!sessionStore.isPlayerOrSpectator || sessionStore.isSeated" :disabled="isSendDisabled" @click="sendMessage">
          <font-awesome-icon :icon="['fas', 'paper-plane']" :title="getTooltipTitle()" />
        </button>
      </div>
    </div>
    <select v-if="!sessionStore.isPlayerOrSpectator && chatStore.activeTab === 'host'" v-model="chatStore.targetPlayer">
      <option v-for="player in playersStore.players" :key="`${player.name}-${player.id}`" :value="player.id">
        {{ player.name }}
      </option>
    </select>
  </MovableDialog>
</template>

<script setup lang="ts">
import { MovableDialog } from "@/components";
import { ref, computed, watch, nextTick } from "vue";
import {
  useAnimationStore,
  useChatStore,
  useGrimoireStore,
  useLocaleStore,
  usePlayersStore,
  useSessionStore,
  useUserPreferencesStore,
  useDialogStore,
} from "@/stores";
import socket from "@/services/socket";
import { ChatMessage } from "@/types";

const animationStore = useAnimationStore();
const chatStore = useChatStore();
const grimoire = useGrimoireStore();
const locale = useLocaleStore();
const playersStore = usePlayersStore();
const sessionStore = useSessionStore();
const dialogStore = useDialogStore();
const userPreferences = useUserPreferencesStore();
const t = locale.t;

const messageInput = ref("");
const messagesContainer = ref<HTMLElement | null>(null);

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const currentPlayerIndex = computed(() => playersStore.currentPlayerIndex);

const getCharacter = (playerId: string) : void|string => {
  if (playerId === "") return;
  if (sessionStore.isPlayerOrSpectator) return;
  const player = playersStore.getById(playerId);
  const roleName = player?.realRole?.name || player?.role?.name
  if (!roleName) return;
  return roleName;
}

const canShowChat = computed(() => {
  return grimoire.isTextChatAllowed && sessionStore.sessionId;
});

const leftNeighbor = computed(() => playersStore.leftNeighbor);

const rightNeighbor = computed(() => playersStore.rightNeighbor);

const activeMessages = computed(() => {
  if (chatStore.activeTab === "host" && grimoire.isSignalHistoryAllowed && sessionStore.isPlayerOrSpectator) {
    return [...chatStore.messages.host, ...chatStore.messages.logs, ...chatStore.messages.system].sort((a, b) => a.timestamp - b.timestamp);
  } else if (chatStore.activeTab === "host" && !sessionStore.isPlayerOrSpectator) {
    return [...chatStore.messages.host, ...chatStore.messages.system].sort((a, b) => a.timestamp - b.timestamp);
  }
  return [...chatStore.messages[chatStore.activeTab], ...chatStore.messages.system].sort((a, b) => a.timestamp - b.timestamp);
});

const activeNeighbor = computed(() => {
  if (chatStore.activeTab === "left") return leftNeighbor.value;
  if (chatStore.activeTab === "right") return rightNeighbor.value;
  return null;
});

const isInputDisabled = computed(() => {
  if (
    chatStore.activeTab === "global" ||
    chatStore.activeTab === "host" ||
    chatStore.activeTab === "logs"
  )
    return false;
  return !activeNeighbor.value || activeNeighbor.value.id === "" || (chatStore.activeTab === "left" || chatStore.activeTab === "right") && grimoire.gamePhase === "firstNight";
});

const isSendDisabled = computed(() => {
  return isCooldown.value || isInputDisabled.value;
});

const getTooltipTitle = () => {
  if (chatStore.activeTab === "global" || chatStore.activeTab === "logs")
    return t("chat.send");
  return activeNeighbor.value && activeNeighbor.value.id !== ""
    ? t("chat.send")
    : t("chat.cannotSend");
};

watch(
  [activeMessages, () => chatStore.open, () => chatStore.activeTab],
  () => {
    if (!chatStore.open) {
      chatStore.activeTabOld &&
        chatStore.messages[chatStore.activeTabOld]?.forEach((msg) => {
          msg.isRead = true;
        });
      chatStore.activeTabOld = null;
    } else if (chatStore.activeTab !== chatStore.activeTabOld) {
      chatStore.activeTabOld &&
        chatStore.messages[chatStore.activeTabOld]?.forEach((msg) => {
          msg.isRead = true;
        });
      chatStore.activeTabOld = chatStore.activeTab;
    }
    if (chatStore.open) {
      scrollToBottom();
    }
  },
  { deep: true },
);

const chatTabKeys = ["left", "global", "host", "whispers", "right"] as const;

const hasChatNotif = computed(() =>
  chatTabKeys
    .filter((t) => !chatStore.open || t !== chatStore.activeTab)
    .some(
      (t) =>
        chatStore.messages[t]?.length && !chatStore.messages[t].at(-1)?.isRead,
    ),
);

const animateMessageSent = () => {
  const neighbor = activeNeighbor.value;
  if (!neighbor) return;
  const fromIndex = currentPlayerIndex.value;
  const toIndex = playersStore.players.indexOf(neighbor);
  animationStore.addAnimation({ from: fromIndex, to: toIndex, emoji: "✉️" });
};

const isCooldown = ref(false);

const startCooldown = (duration: number) => {
  isCooldown.value = true;
  setTimeout(() => {
    isCooldown.value = false;
  }, duration);
};

const sendMessage = () => {
  const t = useLocaleStore().t;
  if (isCooldown.value || !messageInput.value.trim()) return;

  if (chatStore.activeTab === "logs") {
    chatStore.addLog(messageInput.value, "host", t("chat.host"));
    messageInput.value = "";
    scrollToBottom();
    return;
  }

  const chatMessage: ChatMessage = {
    fromId: sessionStore.isPlayerOrSpectator ? sessionStore.playerId : "host",
    fromName: sessionStore.currentPlayerName,
    toId: "",
    toName: "",
    text: messageInput.value,
    isRead: true,
    timestamp: Date.now(),
  };

  if (chatStore.activeTab === "global") {
    chatStore.addMessage("global", chatMessage);
    socket.send("globalChat", chatMessage);
    startCooldown(3000);
  } else if (
    chatStore.activeTab === "host" &&
    sessionStore.isPlayerOrSpectator
  ) {
    chatMessage.toId = "host";
    chatStore.addMessage("host", chatMessage);
    socket.send("direct", { ["host"]: ["chat", chatMessage] });
    startCooldown(800);
  } else if (
    chatStore.activeTab === "host" &&
    !sessionStore.isPlayerOrSpectator
  ) {
    const toPlayer = playersStore.getById(chatStore.targetPlayer);
    if (!toPlayer || toPlayer.id === "") {
      dialogStore.alert(t("chat.cannotSend"));
      return;
    }
    chatMessage.toId = toPlayer.id;
    chatMessage.toName = toPlayer.name;
    chatStore.addMessage("host", chatMessage);
    socket.send("direct", { [chatStore.targetPlayer]: ["chat", chatMessage] });
    startCooldown(800);
  } else {
    const neighbor = activeNeighbor.value;
    if (!neighbor || neighbor.id === "") {
      dialogStore.alert(t("chat.cannotSend"));
      return;
    }
    chatMessage.toId = neighbor.id;
    chatMessage.toName = neighbor.name;
    chatStore.addMessage(chatStore.activeTab, chatMessage);
    socket.send("direct", { [neighbor.id]: ["chat", chatMessage] });
    socket.send("direct", { ["host"]: ["chat", chatMessage] });
    animateMessageSent();

    const activityData = { from: sessionStore.playerId, to: neighbor.id };
    socket.send("chatActivity", activityData);
    startCooldown(800);
  }

  scrollToBottom();
  messageInput.value = "";
};

const handleToggle = () => {
  chatStore.open = !chatStore.open;
};
</script>

<style scoped>
.ack-check {
  color: #999;
  left: 0.5em;
  margin-left: -0.7em;
}

.chat-options {
  display: flex;
  gap: 0.25em;
  margin-bottom: 0.5em;
  flex-direction: column;
}

.player-chat {
  background: var(--background-color);
  backdrop-filter: blur(3px);
  border-radius: 10px;
  border: 3px solid var(--border-color);
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.5));
  color: white;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  padding-inline: 0.25rem;
}

.player-chat.closed .chat-container {
  display: none;
}

.player-chat h3 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.17em;
  margin: 0;
  padding: 0;
  min-height: 30px;
}

.player-chat.notif h3.drag-handle span {
  color: red;
  /* titre en rouge si notif */
}

.player-chat h3 span {
  flex: 1;
}

.player-chat h3 .fa {
  cursor: pointer;
  margin-left: 10px;
  font-size: 1em;
}

label[for] {
  cursor: pointer;
}

.player-chat h3 .fa-times-circle {
  display: block;
}

.player-chat h3 .fa-plus-circle {
  display: none;
}

.player-chat.closed h3 .fa-times-circle {
  display: none;
}

.player-chat.closed h3 .fa-plus-circle {
  display: block;
}

.chat-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  height: 400px;
  width: min(44ch, 80vw);
}

.tabs {
  display: flex;
  gap: 5px;
  border-bottom: 2px solid #666;
  flex-shrink: 0;
}

.tabs .tab {
  flex: 1;
  border: 1px solid grey;
  border-bottom: none;
  border-radius: 5px 5px 0 0;
  padding: 0.15em 1em;
  color: white;
  cursor: pointer;
  transition: background 250ms;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
  position: relative;
}

.tabs .tab:hover {
  background: rgba(255, 255, 255, 0.2);
}

.tabs .tab.active {
  background: linear-gradient(rgb(31, 101, 255) 0%, rgba(0, 0, 0, 0.5) 100%);
}

.tabs .tab.notif:not(.active)::after {
  content: "";
  position: absolute;
  top: 4px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: red;
  border-radius: 50%;
}

.messages {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 0.5rem;
  background: var(--background-color);
  border-radius: 5px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.2);
}

.message {
  background: var(--bg);
  outline: 1px solid var(--bg);
  padding: 0.125rem 0.5rem;
  border-radius: 5px;
  word-wrap: break-word;
  width: fit-content;
  max-width: 80%;
  position: relative;

  &.own {
    --bg: #666;
    align-self: flex-end;
    max-width: 80%;
    margin-right: 0.5rem;
    margin-left: auto;

    &:after {
      content: "";
      position: absolute;
      right: 0;
      top: 50%;
      width: 0;
      height: 0;
      border: 0.4rem solid transparent;
      border-left-color: var(--bg);
      border-right: 0;
      margin-top: -0.4em;
      margin-right: -0.4em;
    }
  }

  &.other {
    --bg: #333;
    align-self: flex-start;
    max-width: 80%;

    &:after {
      content: "";
      position: absolute;
      left: 0;
      top: 50%;
      width: 0;
      height: 0;
      border: 0.4rem solid transparent;
      border-right-color: var(--bg);
      border-left: 0;
      margin-top: -0.4em;
      margin-left: -0.4em;
    }
  }

  &.host {
    --bg: #ddd;
    color: black;
  }

  &.system {
    min-width: 50%;
    margin-inline: auto;
    text-align: center;
    --bg: #ddd;
    color: black;
    &:after {
      border: none;
    }
  }
}

.new-separator {
  display: flex;
  align-items: center;
  text-align: center;
  color: red;
  margin: 10px 0;
}

.new-separator::before,
.new-separator::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid red;
}

.new-separator span {
  padding: 0 10px;
}

select {
  width: 100%;
  font-size: 0.8em;
  margin: 0;
}

.input-area {
  display: flex;
  gap: 5px;
}

.input-area input {
  flex: 1;
  font-size: inherit;
  padding: 0 0.5rem;
  margin: 0;
  border: 1px solid #666;
  border-radius: 5px;
  background: #6666;
  color: white;
}

.input-area input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.input-area input:focus {
  outline: 1px solid white;
}

.input-area button {
  color: white;
  cursor: pointer;
  transition: background 250ms;
}

.input-area button:disabled {
  cursor: not-allowed;
}

.input-area input:disabled {
  cursor: not-allowed;
}
</style>

<style lang="scss">
.player-chat h3 svg:hover path {
  fill: url(#demon);
  stroke-width: 30px;
  stroke: white;
}
</style>
